/**
 * Tokenize a string into word/whitespace runs:
 *   { type: 'word' | 'ws', text }
 */
export function tokenizeRuns(s) {
  const out = [];
  if (!s) return out;
  const re = /(\S+)|(\s+)/g;
  let m;
  while ((m = re.exec(s)) !== null) {
    if (m[1] !== undefined) out.push({ type: 'word', text: m[1] });
    else out.push({ type: 'ws', text: m[2] });
  }
  return out;
}

/**
 * LCS word diff. Returns ops: { type: 'eq' | 'add' | 'del', text }
 */
export function diffWordTokens(A, B) {
  const n = A.length;
  const m = B.length;
  const MAX = 4000;
  if (n > MAX || m > MAX) {
    return [
      ...A.map((t) => ({ type: 'del', text: t })),
      ...B.map((t) => ({ type: 'add', text: t })),
    ];
  }
  const dp = new Array((n + 1) * (m + 1));
  const idx = (i, j) => i * (m + 1) + j;
  for (let i = 0; i <= n; i++) dp[idx(i, 0)] = 0;
  for (let j = 0; j <= m; j++) dp[idx(0, j)] = 0;
  for (let i = 1; i <= n; i++) {
    for (let j = 1; j <= m; j++) {
      if (A[i - 1] === B[j - 1]) {
        dp[idx(i, j)] = dp[idx(i - 1, j - 1)] + 1;
      } else {
        const u = dp[idx(i - 1, j)];
        const l = dp[idx(i, j - 1)];
        dp[idx(i, j)] = u >= l ? u : l;
      }
    }
  }
  const ops = [];
  let i = n;
  let j = m;
  while (i > 0 && j > 0) {
    if (A[i - 1] === B[j - 1]) {
      ops.push({ type: 'eq', text: A[i - 1] });
      i--; j--;
    } else if (dp[idx(i - 1, j)] >= dp[idx(i, j - 1)]) {
      ops.push({ type: 'del', text: A[i - 1] });
      i--;
    } else {
      ops.push({ type: 'add', text: B[j - 1] });
      j--;
    }
  }
  while (i > 0) { ops.push({ type: 'del', text: A[--i] }); }
  while (j > 0) { ops.push({ type: 'add', text: B[--j] }); }
  ops.reverse();
  return ops;
}

function buildWordTokens(runs) {
  const tokens = [];
  let pendingWs = '';
  for (let i = 0; i < runs.length; i++) {
    const r = runs[i];
    if (r.type === 'ws') { pendingWs += r.text; continue; }
    tokens.push({ ws: pendingWs, word: r.text });
    pendingWs = '';
  }
  return { tokens, trailingWs: pendingWs };
}

/**
 * Build the token list for one side of a word-level diff.
 * Returns { tokens: Array<{ ws, word, highlight }>, trailingWs } where
 * `highlight` means the word was added/removed on this side.
 *
 * side: 'original' (deletions shown) | 'submitted' (additions shown)
 */
export function buildDiffTokens({ runs, ops, side }) {
  const sideOpType = side === 'original' ? 'del' : 'add';
  const { tokens, trailingWs } = buildWordTokens(runs);
  const out = [];
  let ti = 0;
  for (let oi = 0; oi < ops.length; oi++) {
    const op = ops[oi];
    if (op.type === 'eq' || op.type === sideOpType) {
      const token = tokens[ti++];
      if (!token) continue;
      out.push({ ws: token.ws, word: token.word, highlight: op.type !== 'eq' });
    }
  }
  return { tokens: out, trailingWs };
}
